/**
 * Free address geocoding via OpenStreetMap's Nominatim service.
 *
 * No API key and no billing required (unlike Google's Geocoding API). This is a
 * natural fit because the NAP Locator map already renders OpenStreetMap tiles.
 *
 * Usage policy (https://operations.osmfoundation.org/policies/nominatim/):
 *   - Max ~1 request/second. We throttle and try at most a couple of queries.
 *   - A valid HTTP Referer is required; the browser sends the app's origin
 *     automatically, which satisfies the policy.
 *   - Intended for interactive, low-volume lookups — which is exactly this
 *     single-address-on-Enter search (NOT bulk geocoding).
 */

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

// Bias results toward Metro Manila so generic street names resolve locally.
// (left, top, right, bottom) — bias only, not a hard restriction.
const METRO_MANILA_VIEWBOX = "120.90,14.78,121.18,14.40";

// Simple client-side throttle to respect the 1 req/sec policy.
let _lastCallAt = 0;
async function throttle() {
  const wait = 1100 - (Date.now() - _lastCallAt);
  if (wait > 0) await new Promise((r) => setTimeout(r, wait));
  _lastCallAt = Date.now();
}

async function queryNominatim(q) {
  await throttle();
  const params = new URLSearchParams({
    q,
    format: "json",
    limit: "1",
    countrycodes: "ph",
    addressdetails: "1",
    viewbox: METRO_MANILA_VIEWBOX,
  });
  try {
    const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) return null;
    const hit = data[0];
    const lat = parseFloat(hit.lat);
    const lng = parseFloat(hit.lon);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng, display_name: hit.display_name || q };
  } catch {
    return null;
  }
}

/**
 * Geocode an address using free OpenStreetMap Nominatim.
 *
 * @param {Object} params
 * @param {string} params.address - Street/house portion
 * @param {string} params.barangay - Barangay name (optional)
 * @param {string} params.municipality - City/municipality (optional)
 * @returns {Promise<{success, latitude, longitude, display_name, message}>}
 */
export async function geocodeAddressFree({ address, barangay, municipality }) {
  const addr = (address || "").trim();
  const brgy = (barangay || "").trim();
  const muni = (municipality || "").trim();

  // Build candidate queries from most specific to least, stopping at the first
  // hit. Each part is comma-joined the way Nominatim expects.
  const candidates = [];
  const join = (...parts) => parts.filter(Boolean).join(", ");

  if (addr) {
    candidates.push(join(addr, brgy, muni, "Metro Manila", "Philippines"));
    candidates.push(join(addr, muni, "Metro Manila", "Philippines"));
  }
  // Fallback to the barangay / city centre so the pin still lands in the area.
  if (brgy && muni) candidates.push(join(brgy, muni, "Metro Manila", "Philippines"));
  if (muni) candidates.push(join(muni, "Metro Manila", "Philippines"));

  // De-duplicate while preserving order.
  const seen = new Set();
  const queries = candidates.filter((q) => q && !seen.has(q) && seen.add(q));

  for (const q of queries) {
    const hit = await queryNominatim(q);
    if (hit) {
      return {
        success: true,
        latitude: hit.lat,
        longitude: hit.lng,
        display_name: hit.display_name,
        message: "Location found.",
      };
    }
  }

  return {
    success: false,
    latitude: null,
    longitude: null,
    display_name: "",
    message: "Location not found.",
  };
}

export default geocodeAddressFree;
